import React, { useEffect, useRef, useState } from "react";
import SignatureCanvas from "react-signature-canvas";
import { api, type Me } from "./api";
import { eastern } from "./RecordScreen";
import { AuthBadge, Thumb, type DocView, type Label, type AttestationView } from "./StationAdded";
import { PathB } from "./AuthorizationPathB";

/**
 * One held medication's authorization (build plan §9.1 #4): Path A, the
 * on-site Supervisor's in-app form and signature; Path B, the remote
 * provider's packet, Teams consult and the returned signed PDF. Everyone may
 * read it; the forms show only for those who may use them (the server checks
 * again).
 */

export interface Order {
  name: string;
  strength: string;
  dose: string;
  route: string;
  windows: string[];
  isPrn: boolean;
  prnInstructions: string;
  frequency: string;
  duration: string;
}

export interface Detail {
  person: { personKey: string; lastName: string; firstName: string; dateOfBirth: string; city: string };
  med: { medKey: string; authorizationStatus: string; status: string; receivedStatus: string; receivedQuantity: string; receivedByEmail: string; receivedAtUtc: string; countVerifiedByEmail: string; controlled: boolean };
  source: string;
  label: Label | null;
  parentLine: string;
  attestation: AttestationView | null;
  photos: DocView[];
  authorizations: DocView[];
  log: { event: string; atUtc: string; byEmail: string; path?: string; decision?: string; provider?: { name: string; credential: string }; signer?: { name: string; credential: string } }[];
  order: Order;
  pendingSinceUtc: string;
  allergies: { confirmed: boolean; lines: string[]; medicationAllergy: string };
  otherMedications: string[];
  windows: { code: string; name: string; startTime: string; endTime: string }[];
  teamsLink: string;
  providerRole: string;
  can: { authorizeOnSite: boolean; attach: boolean; packet: boolean };
}

export const DECISION_LABEL: Record<string, string> = { Authorized: "Authorized", AuthorizedWithChanges: "Authorized with changes", NotAuthorized: "Not authorized" };

export function elapsed(fromUtc: string, nowMs: number): string {
  const t = Date.parse(fromUtc);
  if (Number.isNaN(t)) return "—";
  const m = Math.max(0, Math.floor((nowMs - t) / 60_000));
  const d = Math.floor(m / 1440);
  const h = Math.floor((m % 1440) / 60);
  return d ? `${d} d ${h} h` : `${h} h ${m % 60} m`;
}

/** The decision + order fields both paths share. */
export function DecisionFields({ d, decision, setDecision, order, setOrder, notes, setNotes, idp }: {
  d: Detail;
  decision: string;
  setDecision: (x: string) => void;
  order: Order;
  setOrder: (o: Order) => void;
  notes: string;
  setNotes: (x: string) => void;
  idp: string;
}) {
  const set = (p: Partial<Order>) => setOrder({ ...order, ...p });
  const id = (k: string) => `${idp}-${k}`;
  const authorized = decision !== "NotAuthorized";
  return (
    <>
      <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-sm">
        {Object.entries(DECISION_LABEL).map(([k, l]) => (
          <label key={k} className="flex items-center gap-1.5"><input type="radio" name={id("dec")} checked={decision === k} onChange={() => setDecision(k)} />{l}</label>
        ))}
      </div>
      {authorized && (
        <>
          <div className="mt-2 grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
            <div><label htmlFor={id("n")} className="field-label">Medication</label><input id={id("n")} className="field-input" value={order.name} onChange={(e) => set({ name: e.target.value })} /></div>
            <div><label htmlFor={id("s")} className="field-label">Strength</label><input id={id("s")} className="field-input" value={order.strength} onChange={(e) => set({ strength: e.target.value })} /></div>
            <div><label htmlFor={id("d")} className="field-label">Dose</label><input id={id("d")} className="field-input" value={order.dose} onChange={(e) => set({ dose: e.target.value })} /></div>
            <div><label htmlFor={id("r")} className="field-label">Route</label><input id={id("r")} className="field-input" value={order.route} onChange={(e) => set({ route: e.target.value })} /></div>
            <div><label htmlFor={id("f")} className="field-label">How often</label><input id={id("f")} className="field-input" value={order.frequency} onChange={(e) => set({ frequency: e.target.value })} /></div>
            <div><label htmlFor={id("du")} className="field-label">Duration</label><input id={id("du")} className="field-input" placeholder="For the session" value={order.duration} onChange={(e) => set({ duration: e.target.value })} /></div>
          </div>
          <label className="mt-2 flex items-center gap-2 text-sm"><input type="checkbox" checked={order.isPrn} onChange={(e) => set({ isPrn: e.target.checked })} />As needed (PRN)</label>
          {order.isPrn && <div className="mt-1"><label htmlFor={id("prn")} className="field-label">When to give it</label><input id={id("prn")} className="field-input" value={order.prnInstructions} onChange={(e) => set({ prnInstructions: e.target.value })} /></div>}
          <p className="field-label mt-2">Windows (the medication pass uses these){order.isPrn ? " (optional)" : ""}</p>
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
            {d.windows.map((w) => (
              <label key={w.code} className="flex items-center gap-1.5">
                <input type="checkbox" checked={order.windows.includes(w.code)} onChange={() => set({ windows: order.windows.includes(w.code) ? order.windows.filter((x) => x !== w.code) : [...order.windows, w.code] })} />
                {w.name} ({w.startTime}–{w.endTime})
              </label>
            ))}
          </div>
        </>
      )}
      <label htmlFor={id("no")} className="field-label mt-2">Notes{decision === "Authorized" ? " (optional)" : decision === "NotAuthorized" ? " — why not" : " — what changed"}</label>
      <textarea id={id("no")} className="field-input min-h-16" value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={1000} />
    </>
  );
}

function PathA({ d, me, onDone }: { d: Detail; me: Me; onDone: (d: Detail) => void }) {
  const [decision, setDecision] = useState("Authorized");
  const [order, setOrder] = useState<Order>(d.order);
  const [notes, setNotes] = useState("");
  const [typedName, setTypedName] = useState(me.name);
  const [credential, setCredential] = useState(me.credential);
  const [drawn, setDrawn] = useState("");
  const pad = useRef<SignatureCanvas | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function submit() {
    setBusy(true);
    setError(null);
    const r = await api<{ detail: Detail }>("POST", `/api/authorizations/${encodeURIComponent(d.med.medKey)}/path-a`, { decision, order, notes, signature: { typedName, credential, drawn } });
    setBusy(false);
    if (r.ok) onDone(r.data.detail);
    else setError(r.data.error ?? "That didn't save.");
  }
  return (
    <section className="section-card">
      <h3 className="text-base font-semibold">Path A — on-site Supervisor authorization</h3>
      <p className="text-xs text-slate-500">Confirm or correct the order, decide, and sign. A PDF record is saved to the student's documents.</p>
      <DecisionFields d={d} decision={decision} setDecision={setDecision} order={order} setOrder={setOrder} notes={notes} setNotes={setNotes} idp="pa" />
      <fieldset className="mt-3">
        <legend className="text-sm font-semibold">Signature (both required)</legend>
        <div className="mt-1 grid gap-2 sm:grid-cols-2">
          <div><label htmlFor="pa-tn" className="field-label">Full name, typed</label><input id="pa-tn" className="field-input" value={typedName} onChange={(e) => setTypedName(e.target.value)} /></div>
          <div><label htmlFor="pa-cr" className="field-label">Credential</label><input id="pa-cr" className="field-input" value={credential} onChange={(e) => setCredential(e.target.value)} /></div>
        </div>
        <p className="field-label mt-2">Drawn signature</p>
        <div className="max-w-md rounded border border-slate-300 bg-white">
          <SignatureCanvas ref={(x) => { pad.current = x; }} penColor="#1e293b" canvasProps={{ width: 420, height: 120, className: "h-[120px] w-full rounded" }} onEnd={() => setDrawn(pad.current && !pad.current.isEmpty() ? pad.current.toDataURL("image/png") : "")} />
          <div className="flex justify-end p-1"><button type="button" className="text-xs text-slate-500 underline" onClick={() => { pad.current?.clear(); setDrawn(""); }}>Clear</button></div>
        </div>
      </fieldset>
      {error && <p className="field-error" role="alert">{error}</p>}
      <button type="button" className="btn-primary mt-3" disabled={busy || !drawn || !typedName.trim() || !credential.trim()} onClick={submit}>Sign and save the authorization</button>
    </section>
  );
}

function Summary({ d }: { d: Detail }) {
  const l = d.label;
  return (
    <section className="section-card">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-xl font-semibold">{l ? [l.name, l.strength, l.form].filter(Boolean).join(" ") : d.parentLine || d.order.name}</h2>
        <AuthBadge status={d.med.authorizationStatus} />
      </div>
      <p className="text-sm text-slate-700">
        <a className="text-program underline" href={`/station/${encodeURIComponent(d.person.personKey)}`}>{d.person.lastName}, {d.person.firstName}</a> · {d.person.personKey} · born {d.person.dateOfBirth} · {d.person.city || "Not assigned"}
      </p>
      {d.med.authorizationStatus === "Pending" && <p className="text-sm text-amber-900">Waiting {elapsed(d.pendingSinceUtc, Date.now())} (since {eastern(d.pendingSinceUtc)}). Held: never given until authorized.</p>}
      <div className="mt-2 rounded-md border border-red-300 bg-red-50 p-2 text-sm text-red-950">
        <p className="font-semibold">Allergies{d.allergies.confirmed ? " (confirmed)" : " (family's list, not yet confirmed)"}</p>
        {d.allergies.lines.length ? d.allergies.lines.map((x) => <p key={x}>{x}</p>) : <p>None listed.</p>}
        {d.allergies.medicationAllergy && <p>Medication allergy: {d.allergies.medicationAllergy}</p>}
      </div>
      {l && (
        <p className="mt-2 text-sm">
          Label: {l.dose} · {l.route} · {l.isPrn ? `as needed: ${l.prnInstructions}` : l.frequency}{l.controlled ? " · controlled" : ""}
          <span className="block text-xs text-slate-500">{[l.prescriber && `Prescriber: ${l.prescriber}`, l.pharmacy && `Pharmacy: ${l.pharmacy}`, l.rxNumber && `Rx ${l.rxNumber}`].filter(Boolean).join(" · ")}</span>
        </p>
      )}
      {!l && d.parentLine && <p className="mt-2 text-sm">The parent's entry: {d.parentLine}</p>}
      <p className="text-xs text-slate-500">Received {d.med.receivedStatus}{d.med.receivedQuantity ? `, ${d.med.receivedQuantity} counted` : ""} by {d.med.receivedByEmail} · {eastern(d.med.receivedAtUtc)}{d.med.countVerifiedByEmail ? ` · count verified by ${d.med.countVerifiedByEmail}` : ""}</p>
      {d.attestation && <p className="mt-1 text-sm">Attested by {d.attestation.relationship === "Self" ? `the attendee (${d.attestation.name})` : `${d.attestation.name} (${d.attestation.relationship.toLowerCase()}${d.attestation.phone ? `, ${d.attestation.phone}` : ""})`}: “{d.attestation.text}” — {eastern(d.attestation.atUtc)}, witnessed by {d.attestation.witnessName || d.attestation.witnessEmail}.</p>}
      {d.photos.length > 0 && <div className="mt-2 flex flex-wrap gap-2">{d.photos.map((p) => <Thumb key={p.docKey} doc={p} />)}</div>}
      <p className="mt-2 text-sm font-medium">Other medications</p>
      {d.otherMedications.length ? <ul className="list-disc pl-5 text-sm">{d.otherMedications.map((x) => <li key={x}>{x}</li>)}</ul> : <p className="text-sm text-slate-600">None confirmed.</p>}
      {d.authorizations.length > 0 && (
        <p className="mt-2 text-sm">Authorization documents: {d.authorizations.map((a) => <a key={a.docKey} className="mr-2 text-program underline" href={`/api/med-documents/${encodeURIComponent(a.docKey)}`} target="_blank" rel="noreferrer">{a.captureMethod === "Generated" ? "signed in the app" : "signed PDF"} ({eastern(a.uploadedAtUtc)})</a>)}</p>
      )}
      {d.log.filter((e) => e.event === "decision").map((e) => (
        <p key={e.atUtc} className="mt-1 text-sm">
          <span className="font-medium">{DECISION_LABEL[e.decision ?? ""] ?? e.decision}</span> — Path {e.path}, {e.path === "A" ? `${e.signer?.name}, ${e.signer?.credential}` : `${e.provider?.name}, ${e.provider?.credential} (recorded by ${e.byEmail})`} · {eastern(e.atUtc)}
        </p>
      ))}
    </section>
  );
}

export function Authorization({ path, me }: { path: string; me: Me }): React.ReactElement {
  const medKey = decodeURIComponent(path.slice("/authorizations/".length));
  const [d, setD] = useState<Detail | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    void api<{ detail: Detail }>("GET", `/api/authorizations/${encodeURIComponent(medKey)}`).then((r) => (r.ok ? setD(r.data.detail) : setError(r.data.error ?? "Could not load.")));
  }, [medKey]);
  if (error) return <div className="section-card" role="alert"><p className="text-sm text-red-700">{error}</p></div>;
  if (!d) return <p className="text-sm text-slate-600">Loading…</p>;
  const pending = d.med.authorizationStatus === "Pending";
  return (
    <div>
      <p className="mb-3 text-sm"><a href="/pending" className="text-program underline">← Pending authorizations</a> · <a href={`/station/${encodeURIComponent(d.person.personKey)}`} className="text-program underline">Open the visit</a></p>
      <Summary d={d} />
      {pending && d.can.authorizeOnSite && <PathA key={d.med.medKey} d={d} me={me} onDone={setD} />}
      {pending && <PathB d={d} onDone={setD} />}
      {pending && !d.can.authorizeOnSite && !d.can.attach && <p className="section-card text-sm text-slate-600">A Supervisor (Path A) or an RN (Path B) completes the authorization.</p>}
    </div>
  );
}
